import { Injectable } from '@angular/core';

/**
 * Gives the mobile bottom-sheet dialogs (`.rt-sheet`) their tactile feel: drag the sheet
 * down from the top (grab-handle zone) and, past a threshold, it slides off and closes;
 * release short of it and it springs back.
 *
 * Implemented as one document-level delegated listener rather than a per-dialog directive,
 * so every current and future `.rt-sheet` gets the behaviour with no component wiring. It is
 * viewport-gated (mobile only) and leans on the dialogs' existing "click backdrop to close"
 * to dismiss — no knowledge of each dialog's close handler required.
 */
@Injectable({ providedIn: 'root' })
export class SheetGestureService {
  private readonly mobile = typeof window !== 'undefined'
    ? window.matchMedia('(max-width: 768px)')
    : null;

  /** Only a drag starting within this many px of the sheet top counts as a handle grab. */
  private readonly HANDLE_ZONE = 64;
  /** Drag distance (px) past which release dismisses instead of springing back. */
  private readonly DISMISS_DISTANCE = 120;

  private sheet: HTMLElement | null = null;
  private startY = 0;
  private offset = 0;
  private initialized = false;

  init(): void {
    if (this.initialized || typeof document === 'undefined') return;
    this.initialized = true;
    document.addEventListener('pointerdown', this.onDown, { passive: true });
    document.addEventListener('pointermove', this.onMove, { passive: false });
    document.addEventListener('pointerup', this.onUp, { passive: true });
    document.addEventListener('pointercancel', this.onUp, { passive: true });
  }

  private onDown = (e: PointerEvent): void => {
    if (!this.mobile?.matches || e.button !== 0) return;
    const target = e.target as HTMLElement;
    const sheet = target.closest('.rt-sheet') as HTMLElement | null;
    if (!sheet) return;
    // Don't hijack a tap on a control, and only start from the top grab zone.
    if (target.closest('button, a, input, select, textarea, [role="button"]')) return;
    if (e.clientY - sheet.getBoundingClientRect().top > this.HANDLE_ZONE) return;

    this.sheet = sheet;
    this.startY = e.clientY;
    this.offset = 0;
    sheet.style.transition = 'none';
  };

  private onMove = (e: PointerEvent): void => {
    if (!this.sheet) return;
    const dy = e.clientY - this.startY;
    if (dy <= 0) {
      this.offset = 0;
      this.sheet.style.transform = '';
      return;
    }
    this.offset = dy;
    this.sheet.style.transform = `translateY(${dy}px)`;
    e.preventDefault(); // stop the underlying content from scrolling mid-drag
  };

  private onUp = (): void => {
    const sheet = this.sheet;
    if (!sheet) return;
    this.sheet = null;
    sheet.style.transition = 'transform 0.22s ease';

    if (this.offset > this.DISMISS_DISTANCE) {
      sheet.style.transform = 'translateY(100%)';
      // Reuse the dialog's own "tap backdrop to close" once the slide-out has played.
      const backdrop = sheet.closest('.overlay, .dialog-backdrop') as HTMLElement | null;
      window.setTimeout(() => backdrop?.click(), 200);
    } else {
      sheet.style.transform = ''; // spring back
    }
  };
}
